import { gql, TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { TFunction } from 'i18next';
import { useMemo } from 'react';

import { QueryGetSalaryLandingPageDataArgs, SalaryCalculatorResultDto } from '@pflegia/data-access';
import { DynamicLlpFaqs, useMultipleVariantData } from '@pflegia/feature-flags';
import { JobTitleEnum, JOB_TITLES_TRANSLATIONS, REVERSED_JOB_TITLES_TRANSLATIONS } from '@pflegia/types/api/job-title';
import { LocationObject } from '@pflegia/types/common/location/location-object';
import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import { FaqObject } from '@pflegia/ui/shared/landing-page/faq-page-json-ld/faq-page-json-ld-base';

import { buildDynamicLlpFaqs } from '../utils/build-dynamic-llp-faqs';
import { buildLlpFaqLinks } from '../utils/build-llp-faq-links';
import { LlpFaqData, LlpFaqPageCase, selectLlpFaqs } from '../utils/select-llp-faqs';

export const MAX_LLP_FAQ_EMPLOYERS = 3;

const SALARY_SUMMARY_QUERY: TypedDocumentNode<
  { getSalaryLandingPageData: { summary: Pick<SalaryCalculatorResultDto, 'lowSalary' | 'highSalary' | 'usedFallback'> } | null },
  QueryGetSalaryLandingPageDataArgs
> = gql`
  query GetLlpFaqSalary($input: SalaryLandingPageInput!) {
    getSalaryLandingPageData(input: $input) {
      summary {
        lowSalary
        highSalary
        usedFallback
      }
    }
  }
`;

const VARIANT_TO_PAGE_CASE: Record<string, LlpFaqPageCase | undefined> = {
  jobAndLocation: 'jobAndLocation',
  positionOnly: 'positionOnly',
  locationOnly: 'locationOnly',
};

interface UseDynamicLlpFaqsProps {
  employerNames?: string[];
  faqVariant: string;
  jobTitleId?: string;
  location?: LocationObject;
  locationLabel: string;
  tJobListings: TFunction;
  translatedJobTitle: string;
  vacancyCount?: number;
}

const formatSalary = (value: number) => new Intl.NumberFormat('de-DE').format(value);

export const useDynamicLlpFaqs = ({
  employerNames,
  faqVariant,
  jobTitleId,
  location,
  locationLabel,
  tJobListings,
  translatedJobTitle,
  vacancyCount,
}: UseDynamicLlpFaqsProps): FaqObject[] | null => {
  const { isTest: isEnabled } = useMultipleVariantData(DynamicLlpFaqs.Name);
  const { isPraxia } = useFlavorFromRoute();
  const pageCase = VARIANT_TO_PAGE_CASE[faqVariant];

  // jobAndLocation queries with the city; positionOnly queries nationally (no location).
  const isSalaryQueryablePage = pageCase === 'jobAndLocation' || pageCase === 'positionOnly';
  const salaryLocation = pageCase === 'jobAndLocation' ? (location ?? null) : null;
  const { data: salaryData } = useQuery(SALARY_SUMMARY_QUERY, {
    variables: jobTitleId ? { input: { jobTitleId, location: salaryLocation } } : undefined,
    skip: !isEnabled || !isSalaryQueryablePage || !jobTitleId || (pageCase === 'jobAndLocation' && !location),
  });

  return useMemo(() => {
    if (!isEnabled || !pageCase) return null;

    const jobTitleKey = REVERSED_JOB_TITLES_TRANSLATIONS[translatedJobTitle] as JobTitleEnum | undefined;
    const summary = salaryData?.getSalaryLandingPageData?.summary;

    const data: LlpFaqData = {
      pageCase,
      jobTitleKey,
      hasSalary: Boolean(summary),
      salaryUsedFallback: summary?.usedFallback,
      hasVacancyCount: Boolean(vacancyCount),
      employerNames,
      links: buildLlpFaqLinks({ jobTitleKey, location: locationLabel, translatedJobTitle, isPraxia }),
    };

    return buildDynamicLlpFaqs({
      faqs: selectLlpFaqs(data),
      pageCase,
      tJobListings,
      params: {
        translatedJobTitle,
        translatedJobTitlePlural: jobTitleKey ? JOB_TITLES_TRANSLATIONS[jobTitleKey] : translatedJobTitle,
        location: locationLabel,
        vacancyCount: vacancyCount ? String(vacancyCount) : undefined,
        employerNames: employerNames?.join(', '),
        salaryMin: summary ? formatSalary(summary.lowSalary) : undefined,
        salaryMax: summary ? formatSalary(summary.highSalary) : undefined,
      },
    });
  }, [isEnabled, pageCase, translatedJobTitle, salaryData, employerNames, vacancyCount, locationLabel, tJobListings, isPraxia]);
};
